import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, Clock, Shield, BarChart3 } from "lucide-react";

const statIcons = [TrendingUp, Clock, Shield, BarChart3];
const statValues = [
  { value: 15, suffix: "%" },
  { value: 5, suffix: "" },
  { value: 0, suffix: "%" },
  { value: 8, suffix: "%" },
];
const statKeys = ["annual_growth", "amortization", "tax_advantage", "rental_yield"];

function AnimatedNumber({ value, suffix, inView, label }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) { setCount(value); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <span className="text-4xl lg:text-5xl font-bold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>
      {count}{suffix}{label ? ` ${label}` : ""}
    </span>
  );
}

export default function StatsSection() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const suffixLabels = ["%", i18n.language === "tr" ? " Yil" : i18n.language === "en" ? " Yr" : i18n.language === "ru" ? " Лет" : " سنة", "%", "%"];

  return (
    <section id="stats" data-testid="stats-section" ref={ref} className="relative -mt-16 z-20 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-sm shadow-2xl shadow-[#0A192F]/8 border border-[#E2E8F0]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {statKeys.map((key, i) => {
              const Icon = statIcons[i];
              return (
                <div
                  key={key}
                  data-testid={`stat-card-${i}`}
                  className={`p-8 lg:p-10 text-center ${i < 3 ? "border-b sm:border-b lg:border-b-0 lg:border-r border-[#E2E8F0]" : ""} ${i === 0 ? "sm:border-r" : ""} ${i === 1 ? "sm:border-r sm:border-b lg:border-b-0" : ""}`}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D4AF37]/10 mb-5">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="mb-2">
                    <AnimatedNumber value={statValues[i].value} suffix={suffixLabels[i]} inView={inView} />
                  </div>
                  <h3 className="text-sm font-semibold text-[#0A192F] tracking-wide uppercase mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t(`stats.${key}`)}
                  </h3>
                  <p className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t(`stats.${key}_desc`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
